import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactGA4 from 'react-ga4';
import cx from 'clsx';

import { IconBigAddr } from '@/assets/history';
import { Loading, FilterNoResultComp, Button, Downgrade } from 'components';
import {
  useUpdateChainQueryParam,
  useQueryParams,
  useDeviceTypeIsMobile,
  useMergedConfig,
} from '@/hooks';
import Pagination from 'antd/es/pagination';
import { usePagination } from '@/components/Table/hook';

import { TokenHistoryItem, TokenHistory, ContentScope } from '../types';
import { useTokenHistory } from '../hooks';
import Filter, { Token } from './Filter';
import Row from './Row';
import MobileRow from './MobileRow';

import style from '../index.module.css';

interface ListProps {
  id: string;
  scope: ContentScope;
  className?: string;
  style?: React.CSSProperties;
}

type DisplayList = TokenHistoryItem & {
  projectDict: TokenHistory['project_dict'];
  cexDict: TokenHistory['cex_dict'];
  cateDict: TokenHistory['cate_dict'];
  tokenDict: TokenHistory['token_dict'];
  memoDict: TokenHistory['memo_dict'];
};

export const List = React.memo((props: ListProps) => {
  const isMobile = useDeviceTypeIsMobile();
  const { data: bundleConfig, isLoading: configLoading } = useMergedConfig(props.id);

  return configLoading ? null : isMobile || bundleConfig?.history?.can_load ? (
    <ListInner {...props} />
  ) : (
    <Downgrade />
  );
});

const ListInner = React.memo(({ id, scope, className, style: wrapStyle }: ListProps) => {
  const { t } = useTranslation();

  const isMobile = useDeviceTypeIsMobile();
  const [tokenFilter, setTokenFilter] = useQueryParams('token', '');
  const [chain] = useUpdateChainQueryParam('');
  const [tokenId, name, tokenChain] = tokenFilter.split('_');
  const [isBigAddr, setBigAddr] = useState(false);
  const [filterTokenValue, setFilterTokenValue] = useState(
    tokenId
      ? {
          label: name,
          value: { id: tokenId },
        }
      : null,
  );
  const mountedRef = useRef(false);
  const RowC = isMobile ? MobileRow : Row;

  const {
    data: pageHistories,
    isLoading: isHistoryLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useTokenHistory(scope, id, chain || tokenChain || '', tokenId);

  const [historyList, setHistoryList] = useState<DisplayList[]>([]);

  useEffect(() => {
    if (pageHistories?.length) {
      const histories = pageHistories
        .map(x => {
          const {
            project_dict,
            cex_dict,
            cate_dict,
            token_dict,
            memo_dict,
            history_list: list,
          } = x;

          return list.map(l => ({
            ...l,
            projectDict: project_dict,
            cexDict: cex_dict,
            cateDict: cate_dict,
            tokenDict: token_dict,
            memoDict: memo_dict,
          }));
        })
        .flat();

      setBigAddr(pageHistories[0].is_big_addr);
      setHistoryList(histories);
    }
  }, [pageHistories]);

  useEffect(() => {
    if (mountedRef.current) {
      setTokenFilter('');
      setFilterTokenValue(null);
    }

    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain]);

  const handleFilterChange = useCallback(
    (val: Token | null) => {
      setFilterTokenValue(val ? { label: val.optimized_symbol, value: { id: val.id } } : null);
      setTokenFilter(val ? `${val.id}_${val.optimized_symbol}` : '');
    },
    [setTokenFilter],
  );

  const handleNextClick = () => {
    fetchNextPage();
  };

  const handleTokenClick = useCallback(
    ({ id, name, tokenChain }) => {
      setTokenFilter(chain ? `${id}_${name}` : `${id}_${name}_${tokenChain}`);
      setFilterTokenValue({ label: name, value: { id } });
      if (name) {
        ReactGA4.event({
          category: 'Search',
          action: 'token click search',
          label: name,
        });
      }
    },
    [setTokenFilter, chain],
  );

  const noResult = !historyList?.length;

  return (
    <div className={cx(style.container, className)} style={wrapStyle}>
      {!isMobile && <Filter value={filterTokenValue} onChange={handleFilterChange} chain={chain} />}

      <div className={isMobile ? style.isMobileTable : style.table}>
        {isHistoryLoading ? (
          <Loading className={style.loadingHistory} />
        ) : isBigAddr ? (
          <div className={style.isBigAddr}>
            <IconBigAddr className={style.bigAddrIcon} />
            <span className={style.bigAddrDesc}>{t('components_List.1hwxlfw')}</span>
          </div>
        ) : noResult ? (
          <div style={{ padding: '100px 0' }}>
            <FilterNoResultComp />
          </div>
        ) : (
          <>
            {historyList.map(x => (
              <RowC
                key={x.id}
                onTokenClick={handleTokenClick}
                chain={chain}
                info={x}
                projects={x.projectDict}
                cexs={x.cexDict}
                cate={x.cateDict}
                memos={x.memoDict}
                tokens={x.tokenDict}
                scope={scope}
              />
            ))}
          </>
        )}
      </div>
      {!isBigAddr && hasNextPage ? (
        <Button
          type="primary"
          ghost
          className={style.loadMore}
          onClick={handleNextClick}
          loading={isFetchingNextPage}>
          {t('components_List.1j53tqn')}
        </Button>
      ) : null}
    </div>
  );
});

export const AnalysisHistoryList = React.memo(
  ({
    className,
    style: wrapStyle,
    historyPage,
    isLoading,
    handleTokenClick,
    dictInfo,
  }: {
    className?: string;
    style?: React.CSSProperties;
    historyPage?: TokenHistoryItem[];
    dictInfo?: TokenHistory;
    wrapStyle?: React.CSSProperties;
    handleTokenClick?: (_: { id: string; name: string; tokenChain: string }) => void;
    isLoading?: boolean;
  }) => {
    const [historyList, setHistoryList] = useState<DisplayList[]>();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { pagedData, paginationConfig } = usePagination({
      scrollContainerRef,
      scrollTopOnPageChange: false,
      pageSize: 20,
      pagination: false,
      sortedData: historyList,
    });

    useEffect(() => {
      if (historyPage && dictInfo) {
        const { project_dict, cex_dict, cate_dict, token_dict, memo_dict } = dictInfo;

        const histories = historyPage.map(x => ({
          ...x,
          projectDict: project_dict,
          cexDict: cex_dict,
          cateDict: cate_dict,
          tokenDict: token_dict,
          memoDict: memo_dict,
        }));
        setHistoryList(histories);
      }
    }, [dictInfo, historyPage]);

    const noResult = !historyList?.length;

    return (
      <div className={className} style={wrapStyle}>
        <div className={style.table} ref={scrollContainerRef}>
          {isLoading ? (
            <Loading className={style.loadingHistory} />
          ) : noResult ? (
            <div style={{ padding: '100px 0' }}>
              <FilterNoResultComp />
            </div>
          ) : (
            <>
              {pagedData?.map(x => (
                <Row
                  chain={''}
                  info={x}
                  projects={x.projectDict}
                  cexs={x.cexDict}
                  cate={x.cateDict}
                  memos={x.memoDict}
                  onTokenClick={handleTokenClick}
                  tokens={x.tokenDict}
                  scope={'profile'}
                  key={x.id}
                />
              ))}
              {historyList?.length ? (
                <Pagination className="db-talbe-pagination" size="small" {...paginationConfig} />
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  },
);
